/**
 * Phase 16 Step 13: Health Check presentation — labels, where "Fix →" goes for each problem, and the sidebar badge
 * (one shared poll per tab, every 60 s while the tab is visible).
 */
import { onBeforeUnmount, ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import type { HealthCode, HealthIssue, HealthSeverity, HealthSubjectKind, HealthSummary } from '@codot/contracts';
import { healthApi } from '../api/health.api';
import { i18n, t } from '../i18n';

export const SEVERITY: Record<HealthSeverity, { label: string; dot: string; chip: string; icon: string }> = {
  critical: { get label() { return t('health.sev_critical'); }, dot: 'bg-red-500', chip: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300', icon: '⛔' },
  warning: { get label() { return t('health.sev_warning'); }, dot: 'bg-amber-400', chip: 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300', icon: '⚠️' },
  info: { get label() { return t('health.sev_info'); }, dot: 'bg-sky-400', chip: 'bg-sky-50 text-sky-800 dark:bg-sky-950 dark:text-sky-300', icon: 'ℹ️' },
};
export const KIND_LABEL = new Proxy({} as Record<HealthSubjectKind, string>, { get: (_, k) => t(`health.kind_${String(k)}`) });

/** Short names for each Health Check problem (alert rules, filters). */
export const CODE_LABEL = new Proxy({} as Record<HealthCode, string>, { get: (_, k) => t(`health.code_${String(k)}`) });

/** Where the fix happens, per problem (deep links, not just "the player"). */
export function fixLink(i: HealthIssue): { to: RouteLocationRaw; label: string } {
  const player = (name: string, label: string) => ({ to: { name, params: { screenId: i.subjectId } }, label });
  const byCode: Partial<Record<HealthCode, () => { to: RouteLocationRaw; label: string }>> = {
    PLAYER_OFFLINE: () => player('screen-detail', t('health.fixConnection')),
    PLAYER_NEVER_CONNECTED: () => player('screen-detail', t('health.fixPairing')),
    PLAYER_OUT_OF_SYNC: () => player('screen-detail', t('health.fixSync')),
    PLAYER_FALLBACK: () => player('screen-logs', t('health.fixErrors')),
    PLAYER_NOT_ON_SCREEN: () => player('screen-live', t('health.fixNotOnScreen')),
    PLAYER_FILES_FAILED: () => player('screen-logs', t('health.fixErrors')),
    PLAYER_STORAGE_LOW: () => player('screen-resources', t('health.fixStorage')),
    PLAYER_ERROR_SPIKE: () => player('screen-logs', t('health.fixErrors')),
    PLAYER_APP_OUTDATED: () => player('screen-detail', t('health.fixApp')),
    PLAYER_NO_CONTENT: () => player('screen-published', t('health.fixContent')),
    PLAYER_LOCATION_MISSING: () => ({ to: { name: 'players', query: { q: i.subjectName } }, label: t('health.fixLocation') }),
    CAMPAIGN_EMPTY_ZONE: () => ({ to: { name: 'campaign', params: { campaignId: i.subjectId } }, label: t('health.fixCampaign') }),
    PLAYLIST_EMPTY: () => ({ to: { name: 'playlist', params: { playlistId: i.subjectId } }, label: t('health.fixPlaylist') }),
    MEDIA_FAILED: () => ({ to: { name: 'dashboard', query: { q: i.subjectName } }, label: t('health.fixFind') }),
    PUBLICATION_EXPIRED: () =>
      i.screenId ? { to: { name: 'screen-published', params: { screenId: i.screenId } }, label: t('health.fixList') } : { to: { name: 'groups' }, label: t('health.fixGroups') },
    FEED_FAILING: () => ({ to: { name: 'data-feed', params: { feedId: i.subjectId } }, label: t('health.fixFeed') }),
    // Opens the item in Content (replace its file, or remove it from what's published).
    CONTENT_BROKEN: () => ({ to: { name: 'dashboard', query: { edit: i.subjectId } }, label: t('health.fixItem') }),
    MEDIA_FILE_MISSING: () => ({ to: { name: 'dashboard', query: { edit: i.subjectId } }, label: t('health.fixItem') }),
    PLAYER_CANT_DOWNLOAD: () => player('screen-detail', t('health.fixSync')),
    PLAYER_CONTENT_REJECTED: () => player('screen-logs', t('health.fixErrors')),
  };
  return byCode[i.code]!();
}

/** "3 min", "2 h 10 min", "4 d". */
export function since(iso: string, now = Date.now()): string {
  const m = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  if (m < 60) return t('health.min', { n: m });
  if (m < 48 * 60) return m % 60 ? t('health.hMin', { h: Math.floor(m / 60), m: m % 60 }) : t('health.h', { n: Math.floor(m / 60) });
  return t('health.d', { n: Math.floor(m / 1440) });
}

/* ---------------- sidebar badge ---------------- */

const summary = ref<HealthSummary | null>(null);
let users = 0;
let timer: ReturnType<typeof setInterval> | undefined;
export async function refreshHealthSummary(): Promise<void> {
  try {
    summary.value = await healthApi.summary();
  } catch {
    /* the badge is a hint; never surface an error for it */
  }
}
export function useHealthSummary() {
  if (users++ === 0) {
    void refreshHealthSummary();
    timer = setInterval(() => document.visibilityState === 'visible' && void refreshHealthSummary(), 60_000);
  }
  onBeforeUnmount(() => {
    if (--users === 0) clearInterval(timer);
  });
  return summary;
}

/** The issue in the person's language, rebuilt from its code and details (the server's English is the fallback). */
export function issueMessage(i: Pick<HealthIssue, 'code' | 'details' | 'message'>): string {
  const d = i.details;
  const n = (k: string) => (typeof d[k] === 'number' ? (d[k] as number) : 0);
  switch (i.code) {
    case 'PLAYER_LOCATION_MISSING':
    case 'PLAYER_NEVER_CONNECTED':
    case 'PLAYER_OFFLINE':
    case 'PLAYER_NO_CONTENT':
    case 'PLAYLIST_EMPTY':
      return t(`health.msg_${i.code}`);
    case 'PLAYER_OUT_OF_SYNC':
      return t(d.downloading ? 'health.msg_downloading' : 'health.msg_notLatest');
    case 'PLAYER_FALLBACK':
      {
      const r = String(d.reason ?? 'unknown');
      return t('health.msg_PLAYER_FALLBACK', { reason: i18n.global.te(`screen.fb_${r}`) ? t(`screen.fb_${r}`) : r.replace(/_/g, ' ') });
    }
    case 'PLAYER_FILES_FAILED':
      return t('health.msg_PLAYER_FILES_FAILED', { n: n('failed') }, n('failed'));
    case 'PLAYER_STORAGE_LOW':
      return t('health.msg_PLAYER_STORAGE_LOW', { n: n('freePercent') });
    case 'PLAYER_ERROR_SPIKE':
      return t('health.msg_PLAYER_ERROR_SPIKE', { n: n('errors') }, n('errors'));
    case 'PLAYER_APP_OUTDATED':
      return t('health.msg_PLAYER_APP_OUTDATED', { version: String(d.version ?? ''), newest: String(d.newest ?? '') });
    case 'CAMPAIGN_EMPTY_ZONE':
      return t('health.msg_CAMPAIGN_EMPTY_ZONE', { n: n('emptyZones') }, n('emptyZones'));
    case 'MEDIA_FAILED':
      return t(d.used ? 'health.msg_mediaUsed' : 'health.msg_mediaFailed');
    case 'PUBLICATION_EXPIRED':
      return t('health.msg_PUBLICATION_EXPIRED', { date: String(d.endDate ?? '') });
    case 'FEED_FAILING':
      return t('health.msg_FEED_FAILING', { n: n('failures') }, n('failures'));
    case 'CONTENT_BROKEN': {
      const r = String(d.reason ?? '');
      // Won't play: the player's own reason in plain words (decoder, slow start, format…), with its words after it.
      const cause = d.cause ? String(d.cause) : null;
      const why =
        cause && i18n.global.te(`health.cause_${cause}`)
          ? `${t(`health.cause_${cause}`)}${d.detail ? ` (${String(d.detail)})` : ''}`
          : i18n.global.te(`health.why_${r}`)
            ? t(`health.why_${r}`)
            : r;
      return t(r === 'playback_failed' ? 'health.msg_brokenPlay' : 'health.msg_brokenDownload', { n: n('players'), names: String(d.playerNames ?? ''), why }, n('players'));
    }
    case 'PLAYER_CANT_DOWNLOAD': {
      const code = String(d.syncError ?? 'other');
      const why = i18n.global.te(`sync.err_${code}`) ? t(`sync.err_${code}`) : t('sync.err_other');
      return `${t('health.msg_cantDownload', { why })}${d.detail ? ` (${String(d.detail)})` : ''}`;
    }
    case 'PLAYER_CONTENT_REJECTED':
      return `${t('health.msg_contentRejected')}${d.detail ? ` (${String(d.detail)})` : ''}`;
    case 'MEDIA_FILE_MISSING':
      return d.reason === 'missing' ? t('health.msg_fileMissing') : t('health.msg_fileUnreachable', { detail: String(d.detail ?? '') });
    default:
      return i.message;
  }
}
